import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { makeStyles } from "tss-react/mui";

import { setPaneTab, showDetails } from "@dashboard/devices/store";
import { useReleaseFiles } from "@dashboard/downloads/hooks/useReleaseFiles";
import { ActivateDeviceStep } from "@dashboard/enrollment/components/ActivateDeviceStep";
import { DownloadImageStep } from "@dashboard/enrollment/components/DownloadImageStep";
import { EnrollDeviceStep } from "@dashboard/enrollment/components/EnrollDeviceStep";
import { EnrollDeviceUsing6TapsStep } from "@dashboard/enrollment/components/EnrollDeviceUsing6TapsStep";
import { EnrollmentTypeSelectionStep } from "@dashboard/enrollment/components/EnrollmentTypeSelectionStep";
import { GetInTouchStep } from "@dashboard/enrollment/components/GetInTouchStep";
import { SelectDeviceTypeStep } from "@dashboard/enrollment/components/SelectDeviceTypeStep";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Box } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";

export type EnrollmentPath = "install_android_os" | "install_mdm" | "enroll_existing" | "build_custom";

const useStyles = makeStyles()(() => ({
    content: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "center",
        flex: 1,
        minHeight: 0,
    },
}));

type StepProps = {
    onSkip: () => void;
    onBack: () => void;
    onNext: () => void;
    data: {
        releaseFile: API.ReleaseFile | null;
        releaseFiles: ReturnType<typeof useReleaseFiles>;
        selectedPath: EnrollmentPath;
        onPathSelect: (path: EnrollmentPath) => void;
        onGoToGroupEnrollment: () => void;
        onGoToDevicesEnrollment: () => void;
        onGoToDevices: () => void;
        onContactUs: () => void;
        onSetReleaseFile: (releaseFile: API.ReleaseFile) => void;
    };
};

type EnrollmentStep = {
    key: string;
    render: (props: StepProps) => React.ReactNode;
};

type EnrollmentFlow = EnrollmentStep[];

type EnrollmentFlows = Record<EnrollmentPath, EnrollmentFlow>;

const createFlows = (): EnrollmentFlows => ({
    install_android_os: [
        {
            key: "image-selection",
            render: ({ onSkip, onBack, onNext, data }) => (
                <SelectDeviceTypeStep
                    onSkip={onSkip}
                    onBack={onBack}
                    releaseFiles={data.releaseFiles}
                    onContinue={(releaseFile: API.ReleaseFile) => {
                        data.onSetReleaseFile(releaseFile);
                        onNext();
                    }}
                />
            ),
        },
        {
            key: "download-flash",
            render: ({ onSkip, onBack, onNext, data }) => (
                <DownloadImageStep onSkip={onSkip} onBack={onBack} onContinue={onNext} selectedReleaseFile={data.releaseFile} />
            ),
        },
        {
            key: "activate-device",
            render: ({ onSkip, onBack, data }) => (
                <ActivateDeviceStep onSkip={onSkip} onBack={onBack} onContinue={data.onGoToDevices} />
            ),
        },
    ],

    install_mdm: [
        {
            key: "six-taps",
            render: ({ onSkip, onBack, data }) => (
                <EnrollDeviceUsing6TapsStep onSkip={onSkip} onBack={onBack} onContinue={data.onGoToGroupEnrollment} />
            ),
        },
    ],

    enroll_existing: [
        {
            key: "enroll-existing",
            render: ({ onBack, data }) => <EnrollDeviceStep onBack={onBack} onContinue={data.onGoToDevicesEnrollment} />,
        },
    ],

    build_custom: [
        {
            key: "build-custom",
            render: ({ onBack, data }) => <GetInTouchStep onBack={onBack} onContact={data.onContactUs} />,
        },
    ],
});

const EnrollmentPage = () => {
    const { t } = useTranslation(["pages"]);
    const [currentStep, setCurrentStep] = useState(0);
    const [selectedPath, setSelectedPath] = useState<EnrollmentPath>("install_android_os");
    const [selectedReleaseFile, setSelectedReleaseFile] = useState<API.ReleaseFile | null>(null);

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { data: workspace } = useCurrentWorkspace();
    const releaseFiles = useReleaseFiles();
    const FLOWS = createFlows();

    const handleCancel = () => {
        setCurrentStep(0);
        setSelectedPath("install_android_os");
        setSelectedReleaseFile(null);
    };

    const handleBack = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleContinue = () => {
        setCurrentStep(currentStep + 1);
    };

    const handleGoToGroupEnrollment = () => {
        const defaultGroupId = workspace?.defaultGroupId;

        if (defaultGroupId) {
            dispatch(setPaneTab("ENROLLMENT"));
            dispatch(
                showDetails({
                    type: "group",
                    selectedId: defaultGroupId,
                }),
            );
        }

        navigate("/dashboard/groups?openEnrollmentModal=true");
    };

    const handleGoToDevicesEnrollment = () => {
        navigate("/dashboard/devices?openEnrollmentModal=true");
    };

    const handleGoToDevices = () => {
        navigate("/dashboard/devices");
    };

    const handleContactUs = () => {
        window.open("https://emteria.com/lp/demo", "_blank");
    };

    const handlePathSelect = (path: EnrollmentPath) => {
        setSelectedPath(path);
    };

    const handleSetReleaseFile = (releaseFile: API.ReleaseFile) => {
        setSelectedReleaseFile(releaseFile);
    };

    const renderStep = () => {
        if (currentStep === 0) {
            return (
                <EnrollmentTypeSelectionStep
                    onContinue={handleContinue}
                    onPathSelect={handlePathSelect}
                    selectedPath={selectedPath}
                />
            );
        }

        const sharedStepsCount = 1;
        const flowStepIndex = currentStep - sharedStepsCount;
        const flow = FLOWS[selectedPath];
        const step = flow?.[flowStepIndex];

        if (!step) {
            return null;
        }

        return step.render({
            onSkip: handleCancel,
            onBack: handleBack,
            onNext: handleContinue,
            data: {
                releaseFile: selectedReleaseFile,
                releaseFiles,
                selectedPath,
                onPathSelect: handlePathSelect,
                onGoToGroupEnrollment: handleGoToGroupEnrollment,
                onGoToDevicesEnrollment: handleGoToDevicesEnrollment,
                onGoToDevices: handleGoToDevices,
                onContactUs: handleContactUs,
                onSetReleaseFile: handleSetReleaseFile,
            },
        });
    };

    return (
        <PageContainer fitViewport>
            <PageHeader title={t("pages:enrollmentPage.title")} description={t("pages:enrollmentPage.description")} />
            <ExpiredWrapper>
                <Box className={classes.content}>{renderStep()}</Box>
            </ExpiredWrapper>
        </PageContainer>
    );
};

export default withMetaDescription(EnrollmentPage, {
    title: i18next.t("pages:enrollmentPage.metaTitle"),
    description: i18next.t("pages:enrollmentPage.metaDescription"),
});
